import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2, ShieldAlert } from 'lucide-react';
import { auditPath } from '../utils/auditRoutes';
import { getBackendUrl } from '../utils/apiBase';

const BACKEND_URL = getBackendUrl();

const readAuditSession = () => {
  try {
    const raw = localStorage.getItem('audit_session');
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    localStorage.removeItem('audit_session');
    return null;
  }
};

export const getAuditSessionId = () => readAuditSession()?.session_id || '';

export const auditSessionHeaders = () => {
  const sessionId = getAuditSessionId();
  return sessionId ? { 'X-Audit-Session-Id': sessionId } : {};
};

export function ProtectedAuditRoute({ children }) {
  const location = useLocation();
  const [state, setState] = useState({ loading: true, allowed: false, reason: '' });

  useEffect(() => {
    let cancelled = false;
    const session = readAuditSession();

    if (!session?.session_id) {
      setState({ loading: false, allowed: false, reason: 'missing' });
      return;
    }

    fetch(`${BACKEND_URL}/api/session/validate/${session.session_id}`)
      .then(async (response) => {
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.detail || 'Session is not active');
        }
        return response.json();
      })
      .then(() => {
        if (!cancelled) setState({ loading: false, allowed: true, reason: '' });
      })
      .catch((error) => {
        console.warn('Audit session validation failed:', error.message);
        localStorage.removeItem('audit_session');
        if (!cancelled) setState({ loading: false, allowed: false, reason: error.message });
      });

    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <div className="rounded-2xl border border-border bg-white p-8 shadow-lg text-center max-w-md">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-secondary">Checking your secure audit access</h1>
          <p className="text-muted-foreground mt-2">This protects the paid report and your answers.</p>
        </div>
      </div>
    );
  }

  if (!state.allowed) {
    return <Navigate to={auditPath('/checkout', location.pathname)} replace state={{ blockedFrom: location.pathname }} />;
  }

  return children;
}

export function AuditAccessDeniedNotice() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="rounded-2xl border border-border bg-white p-8 shadow-lg text-center max-w-md">
        <ShieldAlert className="h-10 w-10 text-primary mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-secondary">Secure audit access required</h1>
        <p className="text-muted-foreground mt-2">Please start from checkout so your paid audit session can be verified.</p>
      </div>
    </div>
  );
}
